const normalizeLanguage = (language) => {
  if (!language) return 'en-US'
  const lower = language.toLowerCase()
  if (lower.startsWith('pt')) return 'pt-BR'
  if (lower.startsWith('es')) return 'es'
  return 'en-US'
}

const siteRevampContent = {
  'en-US': {
    nav: {
      about: 'About Us',
      services: 'Services',
      engineering: 'FDE',
      howWeWork: 'How We Work',
      platforms: 'Platforms',
      insights: 'Insights',
      speaking: 'Speaking',
      benjamin: 'Benjamin',
      signIn: 'Sign In',
    },
    footer: {
      product: 'Product',
      company: 'Company',
      legal: 'Legal',
      description:
        'Lean it 101 is a technology and transformation firm: strategy, software delivery and AI engineering services, and the platforms Benjamin and Maestro, built with its own methods.',
      home: 'Home',
      benjamin: 'Benjamin',
      solutions: 'Solutions',
      howItWorks: 'How It Works',
      pricing: 'Pricing',
      bookCall: 'Book a Call',
      story: 'Our Story & Evidence',
      workWithUs: 'Work with us',
      contact: 'Contact',
      privacy: 'Privacy Policy',
      authors: 'Author Rights Policy',
      terms: 'Terms of Use',
      address: 'Av. dos Lagos, 41 - Sala 253, Pedra Branca, Palhoca - Brazil',
      copyright: 'Made in Brazil for teams building modern operations.',
    },
    cta: {
      bookCall: 'Book a Demo',
      talkToBenjamin: 'Talk to Benjamin',
      seeSolutions: 'See solutions',
      seePricing: 'See pricing',
      watchFlow: 'See how it works',
    },
    seo: {
      home: {
        title:
          'Strategic Operations for B2B Tech Companies | Lean it 101',
        description:
          'Lean it 101 helps software and SaaS teams close the gap between strategy and execution through Hoshin Kanri, value stream improvement, and practical AI deployment.',
      },
      benjamin: {
        title: 'What Benjamin Is | Lean it 101',
        description:
          'Benjamin is Lean it 101’s AI agent platform for qualification, support, lead generation, and structured human handoff.',
      },
      solutions: {
        title: 'Benjamin Solutions | Lean it 101',
        description:
          'Start with sales qualification, customer support, or lead generation and expand from one high-value workflow.',
      },
      howItWorks: {
        title: 'How Benjamin Works | Lean it 101',
        description:
          'See how Benjamin connects knowledge, channels, tools, and human handoff into one controlled operational flow.',
      },
      pricing: {
        title: 'Benjamin Pricing | Lean it 101',
        description:
          'Benjamin pricing is scoped around workflow, channels, integrations, and rollout shape, with pilots and scale paths.',
      },
      bookDemo: {
        title: 'Book a Demo with Lean it 101 | Lean it 101',
        description:
          'Choose the path you prefer: send your details or talk to Benjamin to qualify the opportunity and book a demo.',
      },
      story: {
        title: 'About Lean it 101 | Track Record and Team',
        description:
          'See Lean it 101’s product direction, operating philosophy, and selected legacy transformation track record.',
      },
    },
    home: {
      hero: {
        eyebrow: 'Strategic operations for B2B tech companies',
        headline:
          'Close the gap between strategy and execution.',
        body: 'Lean it 101 helps software and SaaS companies align strategy, improve value streams, and deploy AI that fits real operations. Not a pitch deck. Not a vague automation promise.',
        qualifier:
          'Best fit for tech leaders frustrated by strategy that stays in slides, operations that scale by adding people, or AI promises that never connect to actual workflows.',
        panelEyebrow: 'Start with one workflow',
        panelTitle:
          'The best first conversation is about where execution already breaks down.',
        panelPills: [
          'Strategic alignment',
          'Value stream improvement',
          'AI deployment',
        ],
        rotations: [
          'strategic alignment',
          'value stream improvement',
          'AI-assisted operations',
          'facilitation',
          'execution discipline',
        ],
        quickTitle: 'How we work',
        quickBullets: [
          'We work on strategy, operations, and AI as a connected system.',
          'Our track record spans US$9M+ in savings and 80% support tickets reductions.',
          'Benjamin is our AI platform, one part of a broader engineering offer.',
        ],
      },
      proofStrip: [
        'Strategy methods including Hoshin Kanri and value stream mapping',
        'AI deployment grounded in your knowledge base and operational rules',
        'Structured facilitation with measurable improvement targets',
      ],
      buyingBasics: {
        eyebrow: 'Where we start',
        title: 'The best first conversation is about where execution already breaks down.',
        body: 'Most tech teams know where the friction is. Strategy that loses traction after kickoff. Customer operations that grow by headcount instead of by system. AI that pilots and then stalls.',
        cards: [
          {
            title: 'Strategic clarity first',
            body: 'We use structured methods like Hoshin Kanri to connect annual goals to weekly priorities. Strategy that actually reaches the team level.',
          },
          {
            title: 'Value stream before tooling',
            body: 'Before adding automation or AI, we map how work actually flows. Then we remove the drag that no software can fix.',
          },
          {
            title: 'AI that connects to the workflow',
            body: 'Benjamin is our AI deployment layer. It earns its place when it fits a real workflow with real constraints, not as a standalone experiment.',
          },
        ],
      },
      friction: {
        eyebrow: 'Where tech companies get stuck',
        headline:
          'The problem is rarely ambition. It is alignment, flow, and operational discipline.',
        lines: [
          'Strategy gets written in Q1 and ignored by Q3.',
          'Customer operations grow headcount every quarter without fixing the underlying system.',
          'AI pilots run, generate reports, and change nothing in the actual workflow.',
        ],
        body: 'We close those gaps with structured methods, experienced facilitation, and AI tools that fit how your team actually works.',
      },
      solutions: {
        headline: 'Three ways we work with tech teams.',
        body: 'Each one addresses a distinct failure mode. Pick the one where the pain is most obvious today.',
        cards: [
          {
            title: 'Strategic alignment',
            body: 'We deploy Hoshin Kanri and structured planning so goals cascade from leadership to daily execution without distortion.',
            points: [
              'Strategy X-Matrix and breakthrough objectives',
              'Quarterly and weekly alignment cadences',
              'Facilitated planning sessions with your leadership team',
            ],
            href: '/solutions',
          },
          {
            title: 'Value stream improvement',
            body: 'We map how work flows across product, support, and delivery, then redesign for throughput and quality.',
            points: [
              'Value stream mapping and bottleneck analysis',
              'Process redesign with measurable targets',
              'Delivery system governance and metrics',
            ],
            href: '/solutions',
          },
          {
            title: 'AI-assisted operations',
            body: 'Benjamin, our AI platform, handles recurring customer interaction, qualification, and support so your team focuses on the work that needs people.',
            points: [
              'Autonomous customer support and qualification',
              'AI grounded in your knowledge base',
              'Human escalation with preserved context',
            ],
            href: '/benjamin',
          },
        ],
      },
      outcomes: {
        headline: 'What actually improves',
        body: 'The gains we measure: strategic alignment, operational throughput, and reduced front-line drag.',
        items: [
          {
            title: 'Strategy that executes',
            body: 'Priorities stay visible from the leadership level down to what each team works on this week.',
          },
          {
            title: 'Operations that scale',
            body: 'Value streams redesigned for flow, not for heroics. Systems that do not break as you grow.',
          },
          {
            title: 'AI that works in practice',
            body: 'Deployed where repetition is highest. Controlled by your knowledge and rules. Traceable from the start.',
          },
        ],
      },
      benjamin: {
        headline: 'Benjamin: our AI product for customer-facing operations',
        body: 'Benjamin is one part of what Lean it 101 offers. It handles recurring interaction, works across channels, and always escalates to a human when judgment is needed.',
        points: [
          'Built on your knowledge base, not general AI improvisation',
          'Works in web chat, WhatsApp, email, and connected workflows',
          'Qualifies leads, answers support questions, books appointments',
          'Preserves full context when it hands off to a human',
          'Deployable in a 5-day Proof of Concept before full commitment',
          'Part of a broader operational system, with workflows and integrations around it',
        ],
      },
      fit: {
        tag: 'Who we work with',
        title:
          'The fit is clearest when strategy, operations, or AI are on the agenda but not yet working.',
        footer:
          'If you lead a software or SaaS company and need more than a vague AI pitch, this may be the right conversation.',
        cardEyebrow: 'Signals of fit',
        cardTitle: 'Lean it 101 tends to land well when...',
        scenarios: [
          'Your strategy work produces good documents that do not change how the team executes.',
          'Your customer support team grows in headcount every time volume increases.',
          'Your AI pilot ran, people were impressed, and then nothing changed.',
          'Your delivery system depends on key people being available instead of on documented process.',
        ],
        industriesTitle: 'Common contexts',
        industries: [
          'Software and SaaS companies',
          'B2B tech and services',
          'Scale-ups building operational systems',
          'Teams adopting AI for real workflows',
          'Companies doing strategic planning or OKRs',
          'Organizations that have outgrown their current operating model',
        ],
      },
      credibility: {
        headline: 'The track record behind the work',
        body: 'Lean it 101 has operated in complex software and transformation contexts for over a decade. US$9M+ in documented savings, 80% support tickets reductions, and legacy platforms rebuilt for scale sit behind every engagement.',
      },
      final: {
        headline: 'Strategy. Operations. AI. In that order.',
        body: 'If you want to explore whether our work fits your context, the next step is a short conversation about where execution currently breaks down.',
      },
    },
    sharedCta: {
      title: 'See if it is the right fit.',
      body: 'Book a short call the traditional way, or talk to Benjamin first to describe the challenge and get a sense of how we work.',
    },
    benjamin: {
      eyebrow: 'Product',
      title:
        'Benjamin is an AI agent platform for front-line operational work.',
      intro:
        'Use Benjamin where response speed, repetitive interaction, and context handoff matter: sales qualification, support, lead generation, and adjacent workflows.',
      whatIsTitle: 'What Benjamin does well',
      whatIsBody:
        'Benjamin handles repetitive front-line interaction, keeps structured context, uses approved knowledge, and routes work forward when a human should step in.',
      whatNotTitle: 'What Benjamin is not',
      whatNotBody:
        'It is not a toy chatbot, not a giant consulting dependency, and not a promise that humans disappear from the process.',
      pillarsTitle: 'Why teams adopt Benjamin',
      pillars: [
        'To respond faster without hiring purely for first-layer repetition',
        'To keep qualification and support logic more consistent across channels',
        'To stop losing context between chat, email, CRM, and calendar actions',
        'To create a cleaner handoff between automation and humans',
        'To validate AI in one workflow before broad rollout',
        'To reduce operational drag without losing control',
      ],
      cards: [
        {
          title: 'Channels and touchpoints',
          body: 'Benjamin can start on web, continue on email, propose a channel shift, and keep the thread coherent.',
        },
        {
          title: 'Knowledge and rules',
          body: 'Responses can be grounded in approved content, operational constraints, and tool permissions.',
        },
        {
          title: 'Tools and workflow actions',
          body: 'Benjamin can support booking, qualification capture, structured updates, and external system calls when configured.',
        },
        {
          title: 'Human escalation',
          body: 'When the situation needs judgment, Benjamin hands the work off with summary, intent, and state preserved.',
        },
      ],
      finalTitle: 'Start where operational waste is already obvious.',
      finalBody:
        'The best first Benjamin workflow is usually the one where your team already feels delay, repetition, or dropped context every week.',
    },
    howItWorks: {
      eyebrow: 'Operational flow',
      title: 'Benjamin works best when it is attached to a clear workflow.',
      intro:
        'The point is not to spray AI everywhere. The point is to make one meaningful workflow respond faster, carry context better, and require less repetitive human effort.',
      steps: [
        {
          title: 'Pick one workflow',
          body: 'Start with a sales, support, or lead generation bottleneck that already costs time or revenue.',
        },
        {
          title: 'Define knowledge and rules',
          body: 'Load the content, boundaries, and decision logic Benjamin should follow.',
        },
        {
          title: 'Connect the tools that matter',
          body: 'Add calendars, CRM, messaging channels, or other systems only where they change the outcome.',
        },
        {
          title: 'Let Benjamin handle the first layer',
          body: 'Benjamin responds, qualifies, routes, and keeps the thread moving where repetition is highest.',
        },
        {
          title: 'Escalate with context',
          body: 'Humans step in when needed with clearer context and less rework.',
        },
        {
          title: 'Refine before expanding',
          body: 'Measure, adjust, and only widen scope once the workflow is clearly helping.',
        },
      ],
      closeTitle: 'This is how AI becomes operational, not ornamental.',
      closeBody:
        'Benjamin fits teams that want controlled execution, not AI theater.',
    },
    pricing: {
      eyebrow: 'Commercial model',
      title:
        'Benjamin pricing depends on workflow scope, channels, and rollout shape.',
      subtitle:
        'This is not self-serve commodity software with a fake one-size-fits-all price. We scope around the operational workflow you want to improve first.',
      cards: [
        {
          title: 'Pilot path',
          body: 'Best for teams starting with one workflow and validating operational value before expanding.',
        },
        {
          title: 'Scale path',
          body: 'Best for teams already clear on channels, integrations, and the workflow Benjamin should own.',
        },
      ],
      quoteTitle: 'What affects pricing',
      quoteFactors: [
        'Workflow complexity and number of decision branches',
        'Channels involved: web, email, WhatsApp, or mixed',
        'Tool usage such as CRM, calendar, enrichment, or custom APIs',
        'Knowledge base scope and governance needs',
        'Human handoff rules, routing, and reporting requirements',
      ],
      processTitle: 'How buying usually works',
      processSteps: [
        'We align on the workflow and expected outcome.',
        'We define the first scope, channels, and integrations.',
        'We recommend the right activation path: pilot or broader rollout.',
        'We launch the first controlled workflow before expanding.',
      ],
      finalTitle: 'If the workflow matters, we can scope it.',
      finalBody:
        'The fastest path to pricing clarity is a short conversation about the workflow you want Benjamin to own first.',
    },
    solutions: {
      eyebrow: 'Solutions',
      title:
        'Choose the workflow where speed, context, or follow-up is already breaking down.',
      intro:
        'These are the three most common starting points. Each one maps Benjamin to a concrete operational problem your team can improve first.',
      labelProblem: 'Problem',
      labelResponsibility: 'Benjamin handles',
      labelOutcome: 'What improves',
      labelHuman: 'Humans keep',
      viewSolution: 'See solution page',
      cases: [
        {
          slug: 'sales',
          title: 'Sales qualification',
          problem:
            'Inbound demand arrives, but response speed and qualification quality vary by rep, channel, and time of day.',
          responsibility:
            'Benjamin responds, qualifies, proposes next steps, and prepares structured handoff for the sales team.',
          outcome:
            'Faster first response, cleaner pipeline movement, and fewer good leads cooling off in silence.',
          human:
            'Salespeople focus on real opportunity handling, objection work, and closing.',
        },
        {
          slug: 'support',
          title: 'Customer support',
          problem:
            'Support teams spend too much time on repetitive first-layer questions and routing work manually.',
          responsibility:
            'Benjamin answers common questions, handles simple requests, and routes harder cases with context.',
          outcome:
            'More consistent first-layer support, lower repetitive load, and cleaner escalation.',
          human:
            'Humans keep edge cases, judgment calls, and higher-stakes customer conversations.',
        },
        {
          slug: 'lead-generation',
          title: 'Lead generation',
          problem:
            'Prospecting stays manual, lists go stale, and targeting quality depends too much on human time.',
          responsibility:
            'Benjamin helps structure ICP-driven search, prepares lists, and supports the next qualification step.',
          outcome:
            'More targeted lead input with less manual list-building effort.',
          human:
            'Growth and sales teams decide priority, outreach strategy, and commercial follow-up.',
        },
      ],
    },
    bookDemo: {
      eyebrow: 'Next step',
      title: 'Book a demo, your way.',
      links: {
        form: 'or send us your details instead',
        chat: 'or talk to Benjamin instead',
      },
      form: {
        title: 'Book a Demo',
        name: 'Full name',
        phone: 'Phone number',
        email: 'Email',
        role: 'What is your role?',
        rolePlaceholder: 'Select one',
        roleOptions: [
          'Employee',
          'Manager',
          'Director',
          'Founder / Owner',
          'Other',
        ],
        teamSize: 'How many people are on your team?',
        teamSizePlaceholder: 'Select one',
        teamSizeOptions: [
          'Just me',
          '2–10 employees',
          '11–30 employees',
          '31–100 employees',
          '100+ employees',
        ],
        companyType: 'What type of company do you run?',
        companyTypePlaceholder: 'Select one',
        companyTypeOptions: [
          'Software / SaaS',
          'Agency / Studio',
          'Consulting / Professional Services',
          'Financial Services / Insurance',
          'Healthcare',
          'Real Estate / Construction',
          'E-commerce / Retail',
          'Education / Training',
          'Local Business / Franchise',
          'Other',
        ],
        message: 'What do you want to improve? (optional)',
        placeholders: {
          name: 'Your full name',
          phone: '+1 ...',
          email: 'you@company.com',
          message: 'Tell us what you want to improve.',
        },
        submit: 'Book a Demo',
        sending: 'Sending...',
        success:
          'Request received. Our team will review it and contact you at the email provided.',
        error:
          'We could not submit the request right now. Please try again or write to contact@leanit101.com.',
        errors: {
          name: 'Enter your full name.',
          phone: 'Enter a valid phone number.',
          email: 'Enter a valid email address.',
          role: 'Select your role.',
          teamSize: 'Select the size of your team.',
          companyType: 'Select the type of company you run.',
          message: 'Keep your message under 1200 characters.',
        },
      },
      chat: {
        title: 'Talk to Benjamin',
        body: 'Answer a few questions and choose a time directly from the calendar.',
      },
    },
    story: {
      eyebrow: 'Company context',
      title: 'A decade inside operations that had to work.',
      intro:
        'Lean it 101 started in complex software and transformation work, and that is still what we do. We embed senior pods in the operations that hold companies back, and Benjamin is the platform that came out of doing it.',
      cards: [
        {
          title: 'Where we come from',
          body: 'Our roots are in delivery systems, operational improvement, and organizational execution across demanding environments.',
        },
        {
          title: 'Why Benjamin exists now',
          body: 'We wanted a product that could turn operational patterns into repeatable AI-supported execution instead of another consulting dependency.',
        },
        {
          title: 'What we build today',
          body: 'Today the focus is the AI era: Maestro, our operating system for software delivery, and Benjamin, our AI support platform, both built with our own methods.',
        },
      ],
      evidenceTitle: 'Selected legacy track record',
      evidenceIntro:
        'These references are part of our history and capability, not product proof for Benjamin. They show the operational depth behind how we build.',
      evidence: [
        {
          title: 'Vale',
          metric: 'R$50M annual savings',
          body: 'Large-scale transformation work focused on operational redesign, delivery systems, and measurable business results.',
        },
        {
          title: 'Softplan',
          metric: '80% support tickets reduction',
          body: 'Workflow and support improvements that reduced support tickets sharply and eliminated major operational losses.',
        },
        {
          title: 'Brumadinho crisis response',
          metric: 'Cross-functional transformation under pressure',
          body: 'High-stakes coordination and execution in a context where operating discipline mattered more than slideware.',
        },
      ],
      leadershipTitle: 'How we staff the work',
      leadershipRole: 'Senior pods, embedded with your team',
      leadershipBody: [
        'Every engagement is staffed with a small pod of senior engineers who move between business problems, architecture, code, infrastructure and production operations.',
        'There is no long chain of account managers, and no handoff from senior consultants to junior delivery teams. The people who understand the problem are the people who build the solution.',
        'Assessment lands in one to two weeks. The first production delivery follows in two to four, and it grows from there.',
      ],
      practiceTitle: 'Where the practice comes from',
      practiceBody: [
        'Lean it 101 was founded in 2015 by Samuel Crescencio, who has been building software since 1994 and created the Lean Pyramid, the transformation framework behind how we work.',
        'The practice was shaped inside the global Lean and Agile community, including five years on the Agile Alliance board of directors in the United States, working alongside Rebecca Parsons, Diana Larsen and Esther Derby, and learning from Mary and Tom Poppendieck, Henrik Kniberg and Pat Reed.',
        'That lineage shows up as engineering judgement in production, applied at Vale, Softplan, Ambev, Segware, Porter, and in the platform behind Benjamin.',
      ],
      closeTitle: 'Bring us the workflow that hurts the most.',
      closeBody:
        'If you are evaluating Lean it 101 today, evaluate us on the workflows we take into production, the pods we put on them, and the clarity of the engagement.',
    },
  },
  es: {
    nav: {
      about: 'Nosotros',
      services: 'Servicios',
      engineering: 'FDE',
      howWeWork: 'Cómo Trabajamos',
      platforms: 'Plataformas',
      insights: 'Insights',
      speaking: 'Charlas',
      benjamin: 'Benjamin',
      signIn: 'Entrar',
    },
    footer: {
      product: 'Producto',
      company: 'Empresa',
      legal: 'Legal',
      description:
        'Lean it 101 es una firma de tecnología y transformación: servicios de estrategia, entrega de software e ingeniería con IA, y las plataformas Benjamin y Maestro, construidas con sus propios métodos.',
      home: 'Inicio',
      benjamin: 'Benjamin',
      solutions: 'Soluciones',
      howItWorks: 'Cómo funciona',
      pricing: 'Precios',
      bookCall: 'Agendar llamada',
      story: 'Historia y evidencia',
      workWithUs: 'Trabaja con nosotros',
      contact: 'Contacto',
      privacy: 'Política de privacidad',
      authors: 'Política de derechos de autor',
      terms: 'Términos de uso',
      address: 'Av. dos Lagos, 41 - Sala 253, Pedra Branca, Palhoca - Brasil',
      copyright:
        'Hecho en Brasil para equipos que construyen operaciones modernas.',
    },
    cta: {
      bookCall: 'Agendar demo',
      talkToBenjamin: 'Hablar con Benjamin',
      seeSolutions: 'Ver soluciones',
      seePricing: 'Ver precios',
      watchFlow: 'Ver cómo funciona',
    },
    seo: {
      home: {
        title:
          'Operaciones Estratégicas para Empresas de Tecnología B2B | Lean it 101',
        description:
          'Lean it 101 ayuda a equipos de software y SaaS a cerrar la brecha entre estrategia y ejecución con Hoshin Kanri, mejora de flujo de valor y despliegue práctico de IA.',
      },
      benjamin: {
        title: 'Qué es Benjamin | Lean it 101',
        description:
          'Benjamin es la plataforma de agentes de IA de Lean it 101 para calificación, soporte, lead generation y handoff estructurado a humanos.',
      },
      solutions: {
        title: 'Soluciones Benjamin | Lean it 101',
        description:
          'Empieza con calificación comercial, soporte al cliente o generación de leads y expande desde un flujo de alto valor.',
      },
      howItWorks: {
        title: 'Cómo funciona Benjamin | Lean it 101',
        description:
          'Mira cómo Benjamin conecta conocimiento, canales, herramientas y handoff humano en un solo flujo operativo controlado.',
      },
      pricing: {
        title: 'Precios de Benjamin | Lean it 101',
        description:
          'El precio de Benjamin depende del flujo, los canales, las integraciones y la forma del rollout.',
      },
      bookDemo: {
        title: 'Agenda una demo con Lean it 101 | Lean it 101',
        description:
          'Elige el camino que prefieras: enviar tus datos o hablar con Benjamin para avanzar hacia una demo.',
      },
      story: {
        title: 'Sobre Lean it 101 | Historial y equipo',
        description:
          'Conoce la dirección del producto, la filosofía operativa y una parte del historial de transformación de Lean it 101.',
      },
    },
    home: {
      hero: {
        eyebrow: 'Operaciones estratégicas para empresas de tecnología B2B',
        headline:
          'Cierra la brecha entre estrategia y ejecución.',
        body: 'Lean it 101 ayuda a empresas de software y SaaS a alinear su estrategia, mejorar sus flujos de valor y desplegar IA que funciona en operaciones reales. Sin presentaciones vacías. Sin promesas de automatización sin sustancia.',
        qualifier:
          'Ideal para líderes de tecnología frustrados con estrategia que se queda en diapositivas, operaciones que escalan contratando personas en lugar de mejorando el sistema, o promesas de IA que nunca conectan con flujos reales.',
        panelEyebrow: 'Empieza con un flujo',
        panelTitle:
          'La mejor primera conversación es sobre dónde la ejecución ya está fallando.',
        panelPills: [
          'Alineamiento estratégico',
          'Mejora de flujo de valor',
          'Despliegue de IA',
        ],
        rotations: [
          'alineamiento estratégico',
          'mejora de flujo de valor',
          'operaciones con IA',
          'facilitación',
          'disciplina de ejecución',
        ],
        quickTitle: 'Cómo trabajamos',
        quickBullets: [
          'Trabajamos en estrategia, operaciones e IA como un sistema conectado.',
          'Nuestro historial incluye R$50M en ahorros documentados y 80% de reducción de tickets de soporte.',
          'Benjamin es nuestro producto de IA, parte del kit de herramientas, no toda la oferta.',
        ],
      },
      proofStrip: [
        'Métodos estratégicos como Hoshin Kanri y mapeo de flujo de valor',
        'Despliegue de IA respaldado por tu base de conocimiento y reglas operativas',
        'Facilitación estructurada con objetivos de mejora medibles',
      ],
      buyingBasics: {
        eyebrow: 'Por dónde empezamos',
        title: 'La mejor primera conversación es sobre dónde la ejecución ya está fallando.',
        body: 'La mayoría de los equipos de tecnología saben dónde está la fricción. Estrategia que pierde tracción después del kickoff. Operaciones que crecen contratando en lugar de corrigiendo el sistema. IA que pilota y se paraliza.',
        cards: [
          {
            title: 'Claridad estratégica primero',
            body: 'Usamos métodos estructurados como Hoshin Kanri para conectar metas anuales con prioridades semanales. Estrategia que llega de verdad al nivel del equipo.',
          },
          {
            title: 'Flujo de valor antes que herramienta',
            body: 'Antes de automatizar o desplegar IA, mapeamos cómo fluye realmente el trabajo. Luego eliminamos la fricción que ningún software puede resolver.',
          },
          {
            title: 'IA conectada al flujo de trabajo',
            body: 'Benjamin es nuestra capa de despliegue de IA. Gana su lugar cuando encaja en un flujo real con restricciones reales, no como experimento aislado.',
          },
        ],
      },
      friction: {
        eyebrow: 'Dónde se traban las empresas de tecnología',
        headline:
          'El problema rara vez es ambición. Es alineamiento, flujo y disciplina operativa.',
        lines: [
          'La estrategia se escribe en Q1 y se ignora en Q3.',
          'Las operaciones de atención al cliente crecen contratando cada trimestre sin arreglar el sistema de fondo.',
          'Los pilotos de IA corren, generan reportes y nada cambia en el flujo de trabajo real.',
        ],
        body: 'Cerramos esas brechas con métodos estructurados, facilitación con experiencia y herramientas de IA que encajan en cómo tu equipo trabaja de verdad.',
      },
      solutions: {
        headline: 'Tres formas de trabajar con equipos de tecnología.',
        body: 'Cada una aborda un modo de fallo distinto. Elige donde el dolor es más evidente hoy.',
        cards: [
          {
            title: 'Alineamiento estratégico',
            body: 'Desplegamos Hoshin Kanri y planificación estructurada para que los objetivos cascateen desde el liderazgo hasta la ejecución diaria sin distorsión.',
            points: [
              'Matriz X y objetivos de ruptura',
              'Cadencias de alineamiento trimestrales y semanales',
              'Sesiones de planificación facilitadas con tu liderazgo',
            ],
            href: '/solutions',
          },
          {
            title: 'Mejora de flujo de valor',
            body: 'Mapeamos cómo fluye el trabajo a través del producto, soporte y entrega, y luego rediseñamos para throughput y calidad.',
            points: [
              'Mapeo de flujo de valor y análisis de cuellos de botella',
              'Rediseño de procesos con objetivos medibles',
              'Gobernanza y métricas del sistema de entrega',
            ],
            href: '/solutions',
          },
          {
            title: 'Operaciones asistidas por IA',
            body: 'Benjamin, nuestra plataforma de IA, gestiona la interacción recurrente con clientes, calificación y soporte para que tu equipo se enfoque en trabajo de mayor impacto.',
            points: [
              'Soporte y calificación autónoma de clientes',
              'IA respaldada por tu base de conocimiento',
              'Escalación humana con contexto preservado',
            ],
            href: '/benjamin',
          },
        ],
      },
      outcomes: {
        headline: 'Lo que realmente mejora',
        body: 'Los resultados que medimos: alineamiento estratégico, throughput operativo y menor fricción en la primera línea.',
        items: [
          {
            title: 'Estrategia que se ejecuta',
            body: 'Prioridades visibles desde el nivel de liderazgo hasta lo que cada equipo trabaja esta semana.',
          },
          {
            title: 'Operaciones que escalan',
            body: 'Flujos de valor rediseñados para fluir, no para depender de personas clave. Sistemas que no se rompen al crecer.',
          },
          {
            title: 'IA que funciona en la práctica',
            body: 'Desplegada donde la repetición es mayor. Controlada por tu conocimiento y reglas. Trazable desde el primer día.',
          },
        ],
      },
      benjamin: {
        headline: 'Benjamin: nuestro producto de IA para operaciones con clientes',
        body: 'Benjamin es una parte de lo que Lean it 101 ofrece. Gestiona la interacción recurrente, funciona en múltiples canales y siempre escala a un humano cuando se necesita criterio.',
        points: [
          'Construido sobre tu base de conocimiento, no improvisación genérica de IA',
          'Funciona en web chat, WhatsApp, email y flujos conectados',
          'Califica leads, responde soporte, gestiona citas',
          'Preserva contexto completo al hacer handoff a humanos',
          'Desplegable en una Prueba de Concepto de 5 días antes del compromiso total',
          'Parte de un sistema operativo más amplio, con flujos e integraciones alrededor',
        ],
      },
      fit: {
        tag: 'Con quién trabajamos',
        title:
          'El encaje es más claro cuando estrategia, operaciones o IA están en la agenda pero todavía no funcionan.',
        footer:
          'Si lideras una empresa de software o SaaS y necesitas más que una promesa vaga de IA, esta puede ser la conversación correcta.',
        cardEyebrow: 'Señales de encaje',
        cardTitle: 'Lean it 101 suele encajar bien cuando...',
        scenarios: [
          'Tu trabajo de estrategia produce buenos documentos que no cambian cómo ejecuta el equipo.',
          'Tu equipo de soporte crece en headcount cada vez que aumenta el volumen.',
          'Tu piloto de IA corrió, la gente quedó impresionada, y luego nada cambió.',
          'Tu sistema de entrega depende de personas clave disponibles en lugar de proceso documentado.',
        ],
        industriesTitle: 'Contextos comunes',
        industries: [
          'Empresas de software y SaaS',
          'Tecnología y servicios B2B',
          'Scale-ups construyendo sistemas operativos',
          'Equipos adoptando IA para flujos de trabajo reales',
          'Empresas en proceso de planificación estratégica u OKRs',
          'Organizaciones que han crecido más allá de su modelo operativo actual',
        ],
      },
      credibility: {
        headline: 'El historial detrás del trabajo',
        body: 'Lean it 101 ha operado en contextos complejos de software y transformación por más de una década. US$9M+ en ahorros documentados, 80% de reducción de tickets de soporte y plataformas heredadas reconstruidas para escalar respaldan cada proyecto.',
      },
      final: {
        headline:
          'Estrategia. Operaciones. IA. En ese orden.',
        body: 'Si quieres explorar si nuestro trabajo encaja en tu contexto, el próximo paso es una conversación corta sobre dónde la ejecución está fallando hoy.',
      },
    },
    sharedCta: {
      title: 'Mira si es el encaje correcto.',
      body: 'Agenda una llamada corta de la manera tradicional, o habla primero con Benjamin para describir el desafío y tener una idea de cómo trabajamos.',
    },
    benjamin: {
      eyebrow: 'Producto',
      title:
        'Benjamin es una plataforma de agentes de IA para trabajo operativo de primera línea.',
      intro:
        'Usa Benjamin donde la velocidad de respuesta, la interacción repetitiva y el handoff de contexto importan: calificación comercial, soporte, lead generation y flujos cercanos.',
      whatIsTitle: 'Lo que Benjamin hace bien',
      whatIsBody:
        'Benjamin absorbe interacción repetitiva de primera línea, mantiene contexto estructurado, usa conocimiento aprobado y mueve el trabajo hacia adelante cuando un humano debe intervenir.',
      whatNotTitle: 'Lo que Benjamin no es',
      whatNotBody:
        'No es un chatbot de juguete, no es una dependencia gigante de consultoría, ni una promesa de que los humanos desaparecen del proceso.',
      pillarsTitle: 'Por qué los equipos adoptan Benjamin',
      pillars: [
        'Para responder más rápido sin contratar solo para repetición de primera capa',
        'Para hacer más consistente la lógica de calificación y soporte entre canales',
        'Para dejar de perder contexto entre chat, email, CRM y calendario',
        'Para crear un handoff más limpio entre automatización y humanos',
        'Para validar IA en un solo workflow antes de ampliar',
        'Para reducir fricción operativa sin perder control',
      ],
      cards: [
        {
          title: 'Canales y puntos de contacto',
          body: 'Benjamin puede iniciar en web, seguir por email, proponer cambio de canal y mantener el hilo coherente.',
        },
        {
          title: 'Conocimiento y reglas',
          body: 'Las respuestas pueden basarse en contenido aprobado, límites operativos y permisos de herramientas.',
        },
        {
          title: 'Herramientas y acciones',
          body: 'Benjamin puede apoyar agenda, captura de calificación, actualizaciones estructuradas y llamadas a sistemas externos.',
        },
        {
          title: 'Escalación humana',
          body: 'Cuando la situación exige criterio, Benjamin deriva con resumen, intención y estado preservados.',
        },
      ],
      finalTitle: 'Empieza donde el desperdicio operativo ya es evidente.',
      finalBody:
        'El mejor primer workflow para Benjamin suele ser el que tu equipo ya siente como lento, repetitivo o mal traspasado todas las semanas.',
    },
    howItWorks: {
      eyebrow: 'Flujo operativo',
      title: 'Benjamin funciona mejor cuando se conecta a un workflow claro.',
      intro:
        'La idea no es poner IA en todas partes. La idea es hacer que un flujo relevante responda más rápido, cargue mejor el contexto y exija menos esfuerzo humano repetitivo.',
      steps: [
        {
          title: 'Elige un workflow',
          body: 'Empieza por un cuello de botella de ventas, soporte o lead generation que ya te cuesta tiempo o revenue.',
        },
        {
          title: 'Define conocimiento y reglas',
          body: 'Carga el contenido, los límites y la lógica que Benjamin debe seguir.',
        },
        {
          title: 'Conecta las herramientas que importan',
          body: 'Agrega calendario, CRM, canales o APIs solo donde cambien el resultado.',
        },
        {
          title: 'Deja que Benjamin haga la primera capa',
          body: 'Benjamin responde, califica, enruta y mantiene el hilo donde la repetición es más alta.',
        },
        {
          title: 'Escala a humanos con contexto',
          body: 'Las personas entran cuando hace falta con mejor contexto y menos retrabajo.',
        },
        {
          title: 'Refina antes de expandir',
          body: 'Mide, ajusta y amplía alcance solo cuando el workflow ya esté ayudando de forma clara.',
        },
      ],
      closeTitle: 'Así la IA se vuelve operativa, no ornamental.',
      closeBody:
        'Benjamin encaja en equipos que quieren ejecución controlada, no teatro de IA.',
    },
    pricing: {
      eyebrow: 'Modelo comercial',
      title:
        'El precio de Benjamin depende del workflow, los canales y la forma del rollout.',
      subtitle:
        'Esto no es software commodity self-serve con un precio falso para todos. Definimos el alcance según el workflow que quieres mejorar primero.',
      cards: [
        {
          title: 'Ruta piloto',
          body: 'Para equipos que quieren empezar con un solo workflow y validar valor operativo antes de ampliar.',
        },
        {
          title: 'Ruta escala',
          body: 'Para equipos que ya tienen claridad sobre canales, integraciones y el workflow que Benjamin debe asumir.',
        },
      ],
      quoteTitle: 'Qué influye en el precio',
      quoteFactors: [
        'Complejidad del workflow y número de ramas de decisión',
        'Canales involucrados: web, email, WhatsApp o mezcla',
        'Uso de herramientas como CRM, calendario, enriquecimiento o APIs',
        'Alcance de la base de conocimiento y necesidades de gobernanza',
        'Reglas de handoff humano, routing y reporting',
      ],
      processTitle: 'Cómo suele comprarse',
      processSteps: [
        'Alineamos el workflow y el resultado esperado.',
        'Definimos el primer alcance, canales e integraciones.',
        'Recomendamos la ruta correcta: piloto o rollout más amplio.',
        'Lanzamos el primer workflow controlado antes de expandir.',
      ],
      finalTitle: 'Si el workflow importa, podemos dimensionarlo.',
      finalBody:
        'La forma más rápida de aclarar pricing es una conversación corta sobre el workflow que quieres que Benjamin asuma primero.',
    },
    solutions: {
      eyebrow: 'Soluciones',
      title:
        'Elige el workflow donde velocidad, contexto o seguimiento ya se están rompiendo.',
      intro:
        'Estos son los tres puntos de partida más comunes. Cada uno conecta a Benjamin con un problema operativo concreto que tu equipo puede mejorar primero.',
      labelProblem: 'Problema',
      labelResponsibility: 'Benjamin maneja',
      labelOutcome: 'Qué mejora',
      labelHuman: 'Los humanos conservan',
      viewSolution: 'Ver página de solución',
      cases: [
        {
          slug: 'sales',
          title: 'Calificación comercial',
          problem:
            'La demanda inbound llega, pero la velocidad de respuesta y la calidad de la calificación varían demasiado.',
          responsibility:
            'Benjamin responde, califica, propone siguientes pasos y prepara el handoff estructurado al equipo comercial.',
          outcome:
            'Primera respuesta más rápida, pipeline más limpio y menos leads buenos enfriándose en silencio.',
          human:
            'Los vendedores se enfocan en manejar oportunidades reales, objeciones y cierre.',
        },
        {
          slug: 'support',
          title: 'Soporte al cliente',
          problem:
            'El soporte gasta demasiado tiempo en preguntas repetitivas y enrutamiento manual.',
          responsibility:
            'Benjamin responde preguntas comunes, maneja solicitudes simples y deriva casos más complejos con contexto.',
          outcome:
            'Soporte de primera capa más consistente, menos carga repetitiva y escalación más limpia.',
          human:
            'Los humanos conservan edge cases, decisiones complejas y conversaciones sensibles.',
        },
        {
          slug: 'lead-generation',
          title: 'Generación de leads',
          problem:
            'La prospección sigue siendo manual, las listas se ponen viejas y la calidad depende demasiado del tiempo humano.',
          responsibility:
            'Benjamin ayuda a estructurar búsqueda basada en ICP, prepara listas y apoya el siguiente paso de calificación.',
          outcome:
            'Más input relevante para el pipeline con menos esfuerzo manual de prospección.',
          human:
            'Growth y ventas deciden prioridad, estrategia de outreach y seguimiento comercial.',
        },
      ],
    },
    bookDemo: {
      eyebrow: 'Siguiente paso',
      title: 'Agenda una demo a tu manera.',
      links: {
        form: 'o envíanos tus datos',
        chat: 'o habla con Benjamin',
      },
      form: {
        title: 'Agenda una demo',
        name: 'Nombre completo',
        phone: 'Número de teléfono',
        email: 'Email',
        role: '¿Cuál es tu rol?',
        rolePlaceholder: 'Selecciona una opción',
        roleOptions: [
          'Empleado',
          'Manager',
          'Director',
          'Fundador / Dueño',
          'Otro',
        ],
        teamSize: '¿Cuántas personas hay en tu equipo?',
        teamSizePlaceholder: 'Selecciona una opción',
        teamSizeOptions: [
          'Solo yo',
          '2–10 empleados',
          '11–30 empleados',
          '31–100 empleados',
          '100+ empleados',
        ],
        companyType: '¿Qué tipo de empresa diriges?',
        companyTypePlaceholder: 'Selecciona una opción',
        companyTypeOptions: [
          'Software / SaaS',
          'Agencia / Studio',
          'Consultoría / Servicios profesionales',
          'Servicios financieros / Seguros',
          'Salud',
          'Real Estate / Construcción',
          'E-commerce / Retail',
          'Educación / Formación',
          'Negocio local / Franquicia',
          'Otro',
        ],
        message: '¿Qué quieres mejorar? (opcional)',
        placeholders: {
          name: 'Tu nombre completo',
          phone: '+1 ...',
          email: 'tu@empresa.com',
          message: 'Cuéntanos qué quieres mejorar.',
        },
        submit: 'Agenda una demo',
        sending: 'Enviando...',
        success:
          'Solicitud recibida. Nuestro equipo la revisará y te contactará en el email informado.',
        error:
          'No pudimos enviar la solicitud en este momento. Inténtalo nuevamente o escríbenos a contact@leanit101.com.',
        errors: {
          name: 'Ingresa tu nombre completo.',
          phone: 'Ingresa un número de teléfono válido.',
          email: 'Ingresa un email válido.',
          role: 'Selecciona tu rol.',
          teamSize: 'Selecciona el tamaño de tu equipo.',
          companyType: 'Selecciona el tipo de empresa.',
          message: 'Mantén tu mensaje por debajo de 1200 caracteres.',
        },
      },
      chat: {
        title: 'Habla con Benjamin',
        body: 'Responde algunas preguntas y elige un horario directamente en el calendario.',
      },
    },
    story: {
      eyebrow: 'Contexto de la empresa',
      title: 'Una década dentro de operaciones que tenían que funcionar.',
      intro:
        'Lean it 101 nació en trabajo complejo de software y transformación, y eso sigue siendo lo que hacemos. Integramos pods senior en las operaciones que frenan a las empresas, y Benjamin es la plataforma que salió de hacerlo.',
      cards: [
        {
          title: 'De dónde venimos',
          body: 'Nuestras raíces están en sistemas de entrega, mejora operativa y ejecución organizacional en entornos exigentes.',
        },
        {
          title: 'Por qué Benjamin existe ahora',
          body: 'Queríamos un producto que convirtiera patrones operativos en ejecución apoyada por IA, no otra dependencia de consultoría.',
        },
        {
          title: 'Lo que construimos hoy',
          body: 'Hoy el foco es la era de la IA: Maestro, nuestro sistema operativo de la entrega de software, y Benjamin, nuestra plataforma de IA para soporte, ambos construidos con nuestros propios métodos.',
        },
      ],
      evidenceTitle: 'Track record legado seleccionado',
      evidenceIntro:
        'Estas referencias son parte de nuestra historia y capacidad, no prueba de producto para Benjamin. Muestran la profundidad operativa detrás de cómo construimos.',
      evidence: [
        {
          title: 'Vale',
          metric: 'R$50M de ahorro anual',
          body: 'Trabajo de transformación a gran escala enfocado en rediseño operativo, sistemas de entrega y resultados medibles.',
        },
        {
          title: 'Softplan',
          metric: '80% de reducción de tickets de soporte',
          body: 'Mejoras de workflow y soporte que redujeron tickets de soporte de forma drástica y eliminaron pérdidas operativas relevantes.',
        },
        {
          title: 'Respuesta a la crisis de Brumadinho',
          metric: 'Transformación transversal bajo presión',
          body: 'Coordinación y ejecución en un contexto donde la disciplina operativa importaba más que cualquier discurso.',
        },
      ],
      leadershipTitle: 'Cómo formamos los equipos',
      leadershipRole: 'Pods senior, integrados con tu equipo',
      leadershipBody: [
        'Cada proyecto se cubre con un pod pequeño de ingenieros senior que se mueven entre problemas de negocio, arquitectura, código, infraestructura y operaciones en producción.',
        'No hay una larga cadena de account managers, ni traspaso de consultores senior a equipos junior. Quien entiende el problema es quien construye la solución.',
        'El diagnóstico llega en una o dos semanas. La primera entrega en producción llega en dos a cuatro, y crece desde ahí.',
      ],
      practiceTitle: 'De dónde viene la práctica',
      practiceBody: [
        'Lean it 101 fue fundada en 2015 por Samuel Crescencio, que construye software desde 1994 y creó la Pirámide Lean, el marco de transformación detrás de cómo trabajamos.',
        'La práctica se formó dentro de la comunidad global de Lean y Agile, incluyendo cinco años en el board of directors de Agile Alliance en Estados Unidos, junto a Rebecca Parsons, Diana Larsen y Esther Derby, y aprendiendo de Mary y Tom Poppendieck, Henrik Kniberg y Pat Reed.',
        'Ese linaje aparece como criterio de ingeniería en producción, aplicado en Vale, Softplan, Ambev, Segware, Porter y en la plataforma detrás de Benjamin.',
      ],
      closeTitle: 'Tráenos el flujo de trabajo que más duele.',
      closeBody:
        'Si evalúas Lean it 101 hoy, evalúanos por los workflows que llevamos a producción, por los pods que ponemos en ellos y por la claridad del proyecto.',
    },
  },
  'pt-BR': {
    nav: {
      about: 'Sobre',
      services: 'Serviços',
      engineering: 'FDE',
      howWeWork: 'Como Trabalhamos',
      platforms: 'Plataformas',
      insights: 'Insights',
      speaking: 'Palestras',
      benjamin: 'Benjamin',
      signIn: 'Entrar',
    },
    footer: {
      product: 'Produto',
      company: 'Empresa',
      legal: 'Legal',
      description:
        'A Lean it 101 é uma firma de tecnologia e transformação: serviços de estratégia, entrega de software e engenharia com IA, e as plataformas Benjamin e Maestro, construídas com seus próprios métodos.',
      home: 'Home',
      benjamin: 'Benjamin',
      solutions: 'Soluções',
      howItWorks: 'Como Funciona',
      pricing: 'Preços',
      bookCall: 'Agendar reunião',
      story: 'Nossa História e Evidências',
      workWithUs: 'Trabalhe conosco',
      contact: 'Contato',
      privacy: 'Política de Privacidade',
      authors: 'Política de Direitos Autorais',
      terms: 'Termos de Uso',
      address: 'Av. dos Lagos, 41 - Sala 253, Pedra Branca, Palhoça - Brasil',
      copyright:
        'Feito no Brasil para times que estão construindo operações modernas.',
    },
    cta: {
      bookCall: 'Agendar demo',
      talkToBenjamin: 'Falar com o Benjamin',
      seeSolutions: 'Ver soluções',
      seePricing: 'Ver preços',
      watchFlow: 'Ver como funciona',
    },
    seo: {
      home: {
        title:
          'Operações Estratégicas para Empresas de Tecnologia B2B | Lean it 101',
        description:
          'A Lean it 101 ajuda times de software e SaaS a fechar a lacuna entre estratégia e execução com Hoshin Kanri, melhoria de fluxo de valor e implantação prática de IA.',
      },
      benjamin: {
        title: 'O que é o Benjamin | Lean it 101',
        description:
          'Benjamin é a plataforma de agentes de IA da Lean it 101 para qualificação, suporte, lead generation e handoff estruturado para humanos.',
      },
      solutions: {
        title: 'Soluções do Benjamin | Lean it 101',
        description:
          'Comece por qualificação comercial, suporte ao cliente ou geração de leads e expanda a partir de um workflow de alto valor.',
      },
      howItWorks: {
        title: 'Como o Benjamin Funciona | Lean it 101',
        description:
          'Veja como o Benjamin conecta conhecimento, canais, ferramentas e handoff humano em um fluxo operacional controlado.',
      },
      pricing: {
        title: 'Preços do Benjamin | Lean it 101',
        description:
          'O preço do Benjamin depende do workflow, dos canais, das integrações e do formato de rollout.',
      },
      bookDemo: {
        title: 'Agende uma demo com a Lean it 101 | Lean it 101',
        description:
          'Escolha o caminho que preferir: enviar seus dados ou falar com o Benjamin para avançar para uma demo.',
      },
      story: {
        title: 'Sobre a Lean it 101 | Historico e time',
        description:
          'Conheça a direção do produto, a filosofia operacional e parte do histórico de transformação da Lean it 101.',
      },
    },
    home: {
      hero: {
        eyebrow: 'Operações estratégicas para empresas de tecnologia B2B',
        headline:
          'Feche a lacuna entre estratégia e execução.',
        body: 'A Lean it 101 ajuda empresas de software e SaaS a alinhar estratégia, melhorar fluxos de valor e implantar IA que funciona em operações reais. Sem apresentação vaga. Sem promessa de automação sem substância.',
        qualifier:
          'Ideal para líderes de tecnologia frustrados com estratégia que fica nos slides, operações que crescem contratando em vez de consertando o sistema, ou IA que nunca se conecta ao trabalho real.',
        panelEyebrow: 'Comece por um workflow',
        panelTitle:
          'A melhor primeira conversa é sobre onde a execução já está quebrando.',
        panelPills: [
          'Alinhamento estratégico',
          'Melhoria de fluxo de valor',
          'Implantação de IA',
        ],
        rotations: [
          'alinhamento estratégico',
          'melhoria de fluxo de valor',
          'operações com IA',
          'facilitação',
          'disciplina de execução',
        ],
        quickTitle: 'Como trabalhamos',
        quickBullets: [
          'Trabalhamos com estratégia, operações e IA como um sistema conectado.',
          'Nosso histórico inclui R$50M em economia documentada e 80% de redução de tickets de suporte.',
          'O Benjamin é nosso produto de IA, parte do kit de ferramentas, não a oferta toda.',
        ],
      },
      proofStrip: [
        'Métodos estratégicos como Hoshin Kanri e mapeamento de fluxo de valor',
        'Implantação de IA apoiada na sua base de conhecimento e regras operacionais',
        'Facilitação estruturada com metas de melhoria mensuráveis',
      ],
      buyingBasics: {
        eyebrow: 'Por onde começamos',
        title: 'A melhor primeira conversa é sobre onde a execução já está quebrando.',
        body: 'A maioria dos times de tecnologia sabe onde está o atrito. Estratégia que perde tração depois do kickoff. Operações que crescem contratando em vez de corrigindo o sistema. IA que pilota e trava.',
        cards: [
          {
            title: 'Clareza estratégica primeiro',
            body: 'Usamos métodos estruturados como o Hoshin Kanri para conectar metas anuais às prioridades semanais. Estratégia que de fato chega ao nível do time.',
          },
          {
            title: 'Fluxo de valor antes de ferramenta',
            body: 'Antes de automatizar ou implantar IA, mapeamos como o trabalho realmente flui. Depois removemos o atrito que nenhum software consegue resolver.',
          },
          {
            title: 'IA conectada ao workflow',
            body: 'O Benjamin é nossa camada de implantação de IA. Ele ganha espaço quando se encaixa em um workflow real com restrições reais, não como experimento isolado.',
          },
        ],
      },
      friction: {
        eyebrow: 'Onde as empresas de tecnologia travam',
        headline:
          'O problema raramente é ambição. É alinhamento, fluxo e disciplina operacional.',
        lines: [
          'A estratégia é definida no Q1 e ignorada no Q3.',
          'As operações de atendimento crescem contratando a cada trimestre sem consertar o sistema por baixo.',
          'Pilotos de IA rodam, geram relatórios e nada muda no workflow de verdade.',
        ],
        body: 'Fechamos essas lacunas com métodos estruturados, facilitação experiente e ferramentas de IA que se encaixam no jeito que o seu time realmente trabalha.',
      },
      solutions: {
        headline: 'Três formas de trabalhar com times de tecnologia.',
        body: 'Cada uma trata de um modo de falha distinto. Escolha onde a dor é mais evidente hoje.',
        cards: [
          {
            title: 'Alinhamento estratégico',
            body: 'Implantamos o Hoshin Kanri e planejamento estruturado para que as metas cascateiem da liderança para a execução diária sem distorção.',
            points: [
              'Matriz X e objetivos de ruptura',
              'Cadências de alinhamento trimestrais e semanais',
              'Sessões de planejamento facilitadas com a sua liderança',
            ],
            href: '/solutions',
          },
          {
            title: 'Melhoria de fluxo de valor',
            body: 'Mapeamos como o trabalho flui pelo produto, suporte e entrega, depois redesenhamos para throughput e qualidade.',
            points: [
              'Mapeamento de fluxo de valor e análise de gargalos',
              'Redesenho de processos com metas mensuráveis',
              'Governança e métricas do sistema de entrega',
            ],
            href: '/solutions',
          },
          {
            title: 'Operações assistidas por IA',
            body: 'O Benjamin, nossa plataforma de IA, cuida da interação recorrente com clientes, qualificação e suporte para que o seu time foque em trabalho de maior impacto.',
            points: [
              'Suporte e qualificação autônoma de clientes',
              'IA apoiada na sua base de conhecimento',
              'Escalação humana com contexto preservado',
            ],
            href: '/benjamin',
          },
        ],
      },
      outcomes: {
        headline: 'O que de fato melhora',
        body: 'Os ganhos que medimos: alinhamento estratégico, throughput operacional e menos atrito na linha de frente.',
        items: [
          {
            title: 'Estratégia que executa',
            body: 'Prioridades visíveis desde o nível da liderança até o que cada time trabalha nesta semana.',
          },
          {
            title: 'Operações que escalam',
            body: 'Fluxos de valor redesenhados para fluir, não para depender de heróis. Sistemas que não quebram com o crescimento.',
          },
          {
            title: 'IA que funciona na prática',
            body: 'Implantada onde a repetição é maior. Controlada pelo seu conhecimento e regras. Rastreável desde o primeiro dia.',
          },
        ],
      },
      benjamin: {
        headline: 'Benjamin: nosso produto de IA para operações com clientes',
        body: 'O Benjamin é uma parte do que a Lean it 101 oferece. Ele absorve interação recorrente, funciona por vários canais e sempre escala para um humano quando o julgamento é necessário.',
        points: [
          'Construído sobre a sua base de conhecimento, não improvisação de IA genérica',
          'Funciona em web chat, WhatsApp, email e fluxos conectados',
          'Qualifica leads, responde suporte, realiza agendamentos',
          'Preserva contexto completo ao fazer handoff para humanos',
          'Implantável em uma Prova de Conceito de 5 dias antes do compromisso total',
          'Parte de um sistema operacional maior, com workflows e integrações em volta',
        ],
      },
      fit: {
        tag: 'Com quem trabalhamos',
        title:
          'O encaixe fica claro quando estratégia, operações ou IA estão na pauta mas ainda não funcionam.',
        footer:
          'Se você lidera uma empresa de software ou SaaS e precisa de mais do que uma promessa vaga de IA, talvez seja essa a conversa certa.',
        cardEyebrow: 'Sinais de encaixe',
        cardTitle: 'A Lean it 101 costuma encaixar bem quando...',
        scenarios: [
          'Seu trabalho de estratégia gera bons documentos que não mudam como o time executa.',
          'Seu time de suporte cresce em headcount toda vez que o volume aumenta.',
          'Seu piloto de IA rodou, as pessoas ficaram impressionadas, e depois nada mudou.',
          'Seu sistema de entrega depende de pessoas-chave disponíveis em vez de processo documentado.',
        ],
        industriesTitle: 'Contextos comuns',
        industries: [
          'Empresas de software e SaaS',
          'Tecnologia e serviços B2B',
          'Scale-ups construindo sistemas operacionais',
          'Times adotando IA para workflows reais',
          'Empresas em processo de planejamento estratégico ou OKRs',
          'Organizações que cresceram além do modelo operacional atual',
        ],
      },
      credibility: {
        headline: 'O histórico por trás do trabalho',
        body: 'A Lean it 101 opera em contextos complexos de software e transformação há mais de uma década. R$50M em economia documentada, 80% de redução de tickets de suporte e plataformas legadas reconstruídas para escala sustentam cada engajamento.',
      },
      final: {
        headline:
          'Estratégia. Operações. IA. Nessa ordem.',
        body: 'Se você quer explorar se nosso trabalho faz sentido para o seu contexto, o próximo passo é uma conversa curta sobre onde a execução está quebrando hoje.',
      },
    },
    sharedCta: {
      title: 'Veja se faz sentido para o seu contexto.',
      body: 'Agende uma call curta do jeito tradicional ou fale primeiro com o Benjamin para descrever o desafio e ter uma ideia de como trabalhamos.',
    },
    benjamin: {
      eyebrow: 'Produto',
      title:
        'Benjamin é uma plataforma de agentes de IA para trabalho operacional de linha de frente.',
      intro:
        'Use o Benjamin onde velocidade de resposta, interação repetitiva e handoff de contexto fazem diferença: qualificação comercial, suporte, geração de leads e workflows adjacentes.',
      whatIsTitle: 'O que o Benjamin faz bem',
      whatIsBody:
        'O Benjamin absorve interação repetitiva de linha de frente, mantém contexto estruturado, usa conhecimento aprovado e move o trabalho adiante quando um humano precisa entrar.',
      whatNotTitle: 'O que o Benjamin não é',
      whatNotBody:
        'Não é chatbot de brinquedo, não é dependência gigante de consultoria e não é promessa de que os humanos desaparecem do processo.',
      pillarsTitle: 'Por que os times adotam o Benjamin',
      pillars: [
        'Para responder mais rápido sem contratar só para repetição de primeira camada',
        'Para deixar a lógica de qualificação e suporte mais consistente entre canais',
        'Para parar de perder contexto entre chat, email, CRM e agenda',
        'Para criar handoff mais limpo entre automação e humanos',
        'Para validar IA em um workflow antes de ampliar',
        'Para reduzir atrito operacional sem perder controle',
      ],
      cards: [
        {
          title: 'Canais e pontos de contato',
          body: 'O Benjamin pode começar no web chat, seguir por email, propor mudança de canal e manter o fio da conversa coerente.',
        },
        {
          title: 'Conhecimento e regras',
          body: 'As respostas podem se apoiar em conteúdo aprovado, limites operacionais e permissões de ferramentas.',
        },
        {
          title: 'Ferramentas e ações de workflow',
          body: 'O Benjamin pode ajudar com agenda, captura de qualificação, atualizações estruturadas e chamadas a sistemas externos.',
        },
        {
          title: 'Escalação humana',
          body: 'Quando a situação exige julgamento, o Benjamin faz handoff com resumo, intenção e estado preservados.',
        },
      ],
      finalTitle: 'Comece onde o desperdício operacional já é evidente.',
      finalBody:
        'O melhor primeiro workflow para o Benjamin normalmente é aquele em que o seu time já sente atraso, repetição ou perda de contexto toda semana.',
    },
    howItWorks: {
      eyebrow: 'Fluxo operacional',
      title:
        'O Benjamin funciona melhor quando está acoplado a um workflow claro.',
      intro:
        'A ideia não é espalhar IA por tudo. A ideia é fazer um workflow relevante responder mais rápido, carregar melhor o contexto e exigir menos esforço humano repetitivo.',
      steps: [
        {
          title: 'Escolha um workflow',
          body: 'Comece por um gargalo de vendas, suporte ou geração de leads que já custa tempo ou receita.',
        },
        {
          title: 'Defina conhecimento e regras',
          body: 'Carregue o conteúdo, os limites e a lógica que o Benjamin deve seguir.',
        },
        {
          title: 'Conecte as ferramentas que importam',
          body: 'Adicione agenda, CRM, canais ou APIs somente onde isso muda o resultado.',
        },
        {
          title: 'Deixe o Benjamin fazer a primeira camada',
          body: 'O Benjamin responde, qualifica, roteia e mantém o fluxo andando onde a repetição é maior.',
        },
        {
          title: 'Escalone para humanos com contexto',
          body: 'As pessoas entram quando precisam, com mais contexto e menos retrabalho.',
        },
        {
          title: 'Refine antes de expandir',
          body: 'Meça, ajuste e só amplie o escopo quando o workflow já estiver ajudando de forma clara.',
        },
      ],
      closeTitle: 'É assim que IA vira operação, não ornamento.',
      closeBody:
        'O Benjamin faz sentido para times que querem execução controlada, não teatro de IA.',
    },
    pricing: {
      eyebrow: 'Modelo comercial',
      title:
        'O preço do Benjamin depende do workflow, dos canais e do formato do rollout.',
      subtitle:
        'Isso não é software commodity self-serve com um preço genérico. O escopo é definido em cima do workflow que você quer melhorar primeiro.',
      cards: [
        {
          title: 'Caminho piloto',
          body: 'Para times que querem começar por um único workflow e validar valor operacional antes de expandir.',
        },
        {
          title: 'Caminho de escala',
          body: 'Para times que já têm clareza sobre canais, integrações e o workflow que o Benjamin deve assumir.',
        },
      ],
      quoteTitle: 'O que influencia o preço',
      quoteFactors: [
        'Complexidade do workflow e número de ramificações de decisão',
        'Canais envolvidos: web, email, WhatsApp ou combinação',
        'Uso de CRM, agenda, enrichment ou APIs customizadas',
        'Escopo da base de conhecimento e necessidades de governança',
        'Regras de handoff humano, roteamento e reporting',
      ],
      processTitle: 'Como a compra normalmente acontece',
      processSteps: [
        'Alinhamos o workflow e o resultado esperado.',
        'Definimos escopo inicial, canais e integrações.',
        'Recomendamos o caminho certo: piloto ou rollout mais amplo.',
        'Colocamos o primeiro workflow controlado no ar antes de expandir.',
      ],
      finalTitle: 'Se o workflow importa, dá para escopar.',
      finalBody:
        'A forma mais rápida de ter clareza de preço é uma conversa curta sobre o workflow que você quer que o Benjamin assuma primeiro.',
    },
    solutions: {
      eyebrow: 'Soluções',
      title:
        'Escolha o workflow em que velocidade, contexto ou follow-up já estão falhando.',
      intro:
        'Estes são os três pontos de partida mais comuns. Cada um conecta o Benjamin a um problema operacional concreto que o seu time pode melhorar primeiro.',
      labelProblem: 'Problema',
      labelResponsibility: 'Benjamin assume',
      labelOutcome: 'O que melhora',
      labelHuman: 'Os humanos ficam com',
      viewSolution: 'Ver página da solução',
      cases: [
        {
          slug: 'sales',
          title: 'Qualificação comercial',
          problem:
            'A demanda inbound chega, mas a velocidade de resposta e a qualidade da qualificação variam demais entre pessoas e canais.',
          responsibility:
            'O Benjamin responde, qualifica, propõe próximos passos e prepara o handoff estruturado para o time comercial.',
          outcome:
            'Primeira resposta mais rápida, pipeline mais limpo e menos leads bons esfriando em silêncio.',
          human:
            'Os vendedores ficam com o tratamento da oportunidade, objeções e fechamento.',
        },
        {
          slug: 'support',
          title: 'Suporte ao cliente',
          problem:
            'O suporte gasta tempo demais com perguntas repetitivas e encaminhamento manual.',
          responsibility:
            'O Benjamin responde dúvidas comuns, resolve solicitações simples e encaminha casos mais difíceis com contexto.',
          outcome:
            'Primeira camada mais consistente, menos carga repetitiva e escalação mais limpa.',
          human:
            'Os humanos ficam com edge cases, julgamentos delicados e conversas mais sensíveis.',
        },
        {
          slug: 'lead-generation',
          title: 'Geração de leads',
          problem:
            'A prospecção segue manual, as listas envelhecem e a qualidade depende demais do tempo humano.',
          responsibility:
            'O Benjamin ajuda a estruturar busca baseada em ICP, prepara listas e apoia a próxima etapa de qualificação.',
          outcome:
            'Mais input relevante de pipeline com menos esforço manual de prospecção.',
          human:
            'Growth e vendas definem prioridade, estratégia de outreach e seguimento comercial.',
        },
      ],
    },
    bookDemo: {
      eyebrow: 'Próximo passo',
      title: 'Agende uma demo do seu jeito.',
      links: {
        form: 'ou envie seus dados',
        chat: 'ou fale com o Benjamin',
      },
      form: {
        title: 'Agende uma demo',
        name: 'Nome completo',
        phone: 'Número de telefone',
        email: 'Email',
        role: 'Qual é o seu cargo?',
        rolePlaceholder: 'Selecione uma opção',
        roleOptions: [
          'Colaborador',
          'Gerente',
          'Diretor',
          'Fundador / Dono',
          'Outro',
        ],
        teamSize: 'Quantas pessoas há no seu time?',
        teamSizePlaceholder: 'Selecione uma opção',
        teamSizeOptions: [
          'Só eu',
          '2–10 pessoas',
          '11–30 pessoas',
          '31–100 pessoas',
          '100+ pessoas',
        ],
        companyType: 'Que tipo de empresa você opera?',
        companyTypePlaceholder: 'Selecione uma opção',
        companyTypeOptions: [
          'Software / SaaS',
          'Agência / Studio',
          'Consultoria / Serviços profissionais',
          'Serviços financeiros / Seguros',
          'Saúde',
          'Imobiliário / Construção',
          'E-commerce / Varejo',
          'Educação / Treinamento',
          'Negócio local / Franquia',
          'Outro',
        ],
        message: 'O que você quer melhorar? (opcional)',
        placeholders: {
          name: 'Seu nome completo',
          phone: '+55 ...',
          email: 'voce@empresa.com',
          message: 'Conte o que você quer melhorar.',
        },
        submit: 'Agendar demo',
        sending: 'Enviando...',
        success:
          'Solicitação recebida. Nossa equipe vai analisar e entrar em contato pelo email informado.',
        error:
          'Não conseguimos enviar a solicitação agora. Tente novamente ou escreva para contact@leanit101.com.',
        errors: {
          name: 'Informe seu nome completo.',
          phone: 'Informe um número de telefone válido.',
          email: 'Informe um email válido.',
          role: 'Selecione seu cargo.',
          teamSize: 'Selecione o tamanho do seu time.',
          companyType: 'Selecione o tipo da empresa.',
          message: 'Mantenha sua mensagem com no máximo 1200 caracteres.',
        },
      },
      chat: {
        title: 'Fale com o Benjamin',
        body: 'Responda algumas perguntas e escolha um horário direto no calendário.',
      },
    },
    story: {
      eyebrow: 'Contexto da empresa',
      title: 'Uma década dentro de operações que precisavam funcionar.',
      intro:
        'A Lean it 101 nasceu em trabalho complexo de software e transformação, e é isso que seguimos fazendo. Colocamos pods seniores dentro das operações que travam as empresas, e o Benjamin é a plataforma que saiu disso.',
      cards: [
        {
          title: 'De onde viemos',
          body: 'As nossas raízes estão em sistemas de entrega, melhoria operacional e execução organizacional em ambientes exigentes.',
        },
        {
          title: 'Por que o Benjamin existe agora',
          body: 'Queríamos um produto que transformasse padrões operacionais em execução apoiada por IA, em vez de mais uma dependência de consultoria.',
        },
        {
          title: 'O que construimos hoje',
          body: 'Hoje o foco é a era da IA: o Maestro, nosso sistema operacional da entrega de software, e o Benjamin, nossa plataforma de IA para suporte, ambos construídos com nossos próprios métodos.',
        },
      ],
      evidenceTitle: 'Track record legado selecionado',
      evidenceIntro:
        'Essas referências fazem parte da nossa história e da nossa capacidade, não são prova de produto do Benjamin. Elas mostram a profundidade operacional por trás da forma como construímos.',
      evidence: [
        {
          title: 'Vale',
          metric: 'R$ 50 mi de economia anual',
          body: 'Trabalho de transformação em larga escala com foco em redesenho operacional, sistemas de entrega e resultado de negócio mensurável.',
        },
        {
          title: 'Softplan',
          metric: '80% de redução de tickets de suporte',
          body: 'Melhorias de workflow e suporte que reduziram tickets de suporte de forma drástica e eliminaram perdas operacionais relevantes.',
        },
        {
          title: 'Resposta à crise de Brumadinho',
          metric: 'Transformação transversal sob pressão',
          body: 'Coordenação e execução em um contexto no qual disciplina operacional importava mais do que qualquer slide bonito.',
        },
      ],
      leadershipTitle: 'Como montamos os times',
      leadershipRole: 'Pods seniores, integrados ao seu time',
      leadershipBody: [
        'Cada engajamento é coberto por um pod pequeno de engenheiros seniores que transitam entre problemas de negócio, arquitetura, código, infraestrutura e operação em produção.',
        'Não existe uma longa cadeia de account managers, nem repasse de consultores seniores para times juniores. Quem entende o problema é quem constrói a solução.',
        'O diagnóstico sai em uma a duas semanas. A primeira entrega em produção vem em duas a quatro, e cresce a partir dali.',
      ],
      practiceTitle: 'De onde vem a prática',
      practiceBody: [
        'A Lean it 101 foi fundada em 2015 por Samuel Crescêncio, que constrói software desde 1994 e criou a Pirâmide Lean, o framework de transformação por trás da forma como trabalhamos.',
        'A prática foi formada dentro da comunidade global de Lean e Agile, incluindo cinco anos no board of directors da Agile Alliance nos Estados Unidos, ao lado de Rebecca Parsons, Diana Larsen e Esther Derby, e aprendendo com Mary e Tom Poppendieck, Henrik Kniberg e Pat Reed.',
        'Essa linhagem aparece como critério de engenharia em produção, aplicado na Vale, Softplan, Ambev, Segware, Porter e na plataforma por trás do Benjamin.',
      ],
      closeTitle: 'Traga o workflow que mais dói.',
      closeBody:
        'Se você está avaliando a Lean it 101 hoje, avalie pelos workflows que levamos para produção, pelos pods que colocamos neles e pela clareza do engajamento.',
    },
  },

}

export const getSiteRevampContent = (language) =>
  siteRevampContent[normalizeLanguage(language)] || siteRevampContent['en-US']

export { normalizeLanguage }
